import { FC, useMemo } from "react";
import { UseFormReturn } from "react-hook-form";
import { makeStyles } from "tss-react/mui";
import { MiDetailFields, MiIcon } from "@miplatform/components";
import { INPUT_TYPES } from "@miplatform/constants";
import {
  IAccountListResponse,
  ICommunityListItemResponse,
  IInspectionAuthorityResponse,
} from "@miplatform/types";
import {
  mdiAccountHardHat,
  mdiClipboardSearchOutline,
  mdiHomeGroup,
} from "@mdi/js";
import { Box, Card, Typography, useTheme } from "@mui/material";
import { ConfigAccordionHeader } from "../../../../../Config/components/ConfigAccordionHeader/ConfigAccordionHeader";
import { IAddRequirementsData } from "../../schema/addRequirements.schema";

const REQUIREMENT_TYPES = [
  {
    value: "Inspection" as const,
    label: "Inspection Requirement",
    icon: mdiClipboardSearchOutline,
  },
  {
    value: "Community" as const,
    label: "Community Requirement",
    icon: mdiHomeGroup,
  },
  {
    value: "Builder" as const,
    label: "Builder Requirement",
    icon: mdiAccountHardHat,
  },
];

export const RequirementDetailsTab: FC<RequirementDetailsTabProps> = ({
  form,
  extraTabProps,
}) => {
  const {
    inspectionAuthorities = [],
    communities = [],
    accounts = [],
    isReadOnly = false,
  } = extraTabProps || {};

  const theme = useTheme();
  const { classes, cx } = useStyles();
  const { control, watch, setValue } = form;
  const selectedType = watch("type");

  const dynamicDropdown = useMemo(() => {
    if (selectedType === "Inspection") {
      return {
        label: "Inspection Authority",
        name: "inspectionAuthorityId" as const,
        options: inspectionAuthorities.map((a) => ({
          label: a.name,
          value: a.id,
        })),
      };
    }
    if (selectedType === "Community") {
      return {
        label: "Community",
        name: "communityId" as const,
        options: communities.map((c) => ({
          label: c.name,
          value: c.id,
        })),
      };
    }
    return {
      label: "Customer",
      name: "accountId" as const,
      options: accounts.map((a) => ({
        label: a.name,
        value: a.id,
      })),
    };
  }, [selectedType, inspectionAuthorities, communities, accounts]);

  const detailFields = [
    {
      label: "Requirement Group Name",
      name: "name",
      required: true,
      formControl: control,
      disabled: isReadOnly,
      width: "100%",
    },
    {
      label: dynamicDropdown.label,
      name: dynamicDropdown.name,
      fieldType: INPUT_TYPES.SELECT,
      formControl: control,
      disabled: isReadOnly,
      required: true,
      options: dynamicDropdown.options,
      shrink: true,
      width: "100%",
    },
  ];

  return (
    <>
      <ConfigAccordionHeader title="Requirement Type">
        <Box display="flex" gap={2} p={1}>
          {REQUIREMENT_TYPES.map(({ value, label, icon }) => {
            const isSelected = selectedType === value;
            return (
              <Card
                key={value}
                onClick={() => {
                  if (!isReadOnly) {
                    setValue("type", value, {
                      shouldValidate: true,
                      shouldDirty: true,
                    });
                  }
                }}
                className={cx(classes.typeCard, {
                  [classes.typeCardSelected]: isSelected,
                  [classes.typeCardReadOnly]: isReadOnly,
                })}
              >
                <MiIcon
                  path={icon}
                  color={
                    isSelected
                      ? theme.palette.primary.main
                      : theme.palette.grey[500]
                  }
                  size={1}
                />
                <Typography
                  variant="HD3"
                  color={isSelected ? "primary.main" : "textPrimary"}
                  textAlign="center"
                >
                  {label}
                </Typography>
              </Card>
            );
          })}
        </Box>
      </ConfigAccordionHeader>
      <ConfigAccordionHeader title="Enter Requirement Group Details">
        <MiDetailFields
          detailFields={detailFields}
          headerProps={{
            subtitle: "Enter requirement details below.",
            variant: "text",
          }}
          customWrapperStyle={{ rowGap: "8px" }}
        />
      </ConfigAccordionHeader>
    </>
  );
};

interface RequirementDetailsTabProps {
  form: UseFormReturn<IAddRequirementsData>;
  extraTabProps?: {
    inspectionAuthorities?: IInspectionAuthorityResponse[];
    communities?: ICommunityListItemResponse[];
    accounts?: IAccountListResponse[];
    isReadOnly?: boolean;
  };
}

const useStyles = makeStyles()((theme) => ({
  typeCard: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: theme.spacing(1),
    padding: `${theme.spacing(2)} ${theme.spacing(3)}`,
    cursor: "pointer",
    flex: 1,
    border: `2px solid ${theme.palette.grey.accent}`,
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.spacing(1),
    boxShadow: "none",
    transition: "border-color 0.15s, background-color 0.15s",
    "&:hover": {
      borderColor: theme.palette.primary.main,
    },
  },
  typeCardSelected: {
    border: `2px solid ${theme.palette.primary.main}`,
    backgroundColor: `${theme.palette.primary.main}14`,
  },
  typeCardReadOnly: {
    cursor: "default",
    "&:hover": {
      borderColor: theme.palette.grey.accent,
    },
  },
}));
